import React from "react";
import { Label } from "semantic-ui-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faUserHardHat, faFileAlt, faDollarSign, faPercentage, faCogs, faKey, faBars } from "@fortawesome/pro-solid-svg-icons";

import { TYRE_POSITION, getTyreItemName } from "./tyre";

export const QUESTION_RESULT_STATUS = {
  NO_STATUS: -1,
  OK: 0,
  OK_WITH_REMARKS: 1,
  ADVISED: 2,
  NECESSARY: 3,
  NOT_WORKED_ON: 4,
};

export const INTERVENTION_ELEMENT_TYPE = {
  TASK: 1,
  LABOR: 2,
  PART: 3,
  TEXT: 4,
  DISCOUNT: 5,
  SMALL_PART: 6,
  FEE: 7,
  RENTAL: 8,
  MENU: 9,
};

export const APPOINTMENT_STATUSES = {
  CANCELED: -1,
  NEW_CAR: 1,
  CAR_CHECK: 2,
  PRICING_OK: 3,
  CUSTOMER_OK: 4,
  CAR_READY: 5,
  DIAGNOSE: 6,
  AANNAME_OK: 7,
  CALCULATING: 8,
  CONTACT: 9,
  CAR_CHECK_STARTED: 10,
  QUALITY_CHECK: 11,
  CUSTOMER_ANSWERED: 12,
  LAST_REAL_STATUS: 12,
  BACK_ORDER: 13,
  HANDLE_CHECK_IN: 50,
  CHECK_IN_DONE: 51,
  CAR_IN_SHOP: 52,
  CAR_OK_PLUS_REPAIR_OVERVIEW: 53,
  QUALITY_CHECK_PLUS_REPAIR_OVERVIEW: 54,
  CAR_OUT_OF_SHOP: 55,
};

export const PIN_TYPE = {
  REMARKS: 1,
  WARRANTY: 2,
  RECALL: 3,
};

export const CHECKLIST_TYPES = {
  First: 0,
  Regular: 0,
  Aanname: 1,
  Quality: 2,
  Info: 3,
  ExtraParts: 4,
  Deprecated: 5,
  TestDrive: 6,
  Warranty: 7,
  Last: 7,
};

export const ROLES = {
  SUPER_ADMIN: 10,
  CLAIRE_ADMIN: 20,
  SUPPORT_ADMIN: 25,
  CONSULTANT: 30,
  DEALER_ADMIN: 40,
  EXACT_ADMIN: 45,
  RECEPTIONIST: 50,
  CHIEF_MECHANIC: 55,
  MECHANIC: 60,
  CALL_CENTER: 70,
  SUPERVISOR: 80,
  WARRANTY_MANAGER: 90,
  COUNTER_TABLET: 100,
  MANUFACTURER: 110,
};

export const renderInterventionElementType = type => {
  switch (type) {
    case INTERVENTION_ELEMENT_TYPE.PART:
      return (
        <Label>
          <FontAwesomeIcon icon={faCog} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.LABOR:
      return (
        <Label>
          <FontAwesomeIcon icon={faUserHardHat} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.TEXT:
      return (
        <Label>
          <FontAwesomeIcon icon={faFileAlt} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.DISCOUNT:
      return (
        <Label>
          <FontAwesomeIcon icon={faPercentage} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.SMALL_PART:
      return (
        <Label>
          <FontAwesomeIcon icon={faCogs} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.FEE:
      return (
        <Label>
          <FontAwesomeIcon icon={faDollarSign} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.RENTAL:
      return (
        <Label>
          <FontAwesomeIcon icon={faKey} />
        </Label>
      );

    case INTERVENTION_ELEMENT_TYPE.MENU:
      return (
        <Label>
          <FontAwesomeIcon icon={faBars} />
        </Label>
      );

    default:
      return null;
  }
};

export function round2(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

const getVATRate = (itemVat, settings) => {
  if (itemVat !== null && itemVat !== undefined) return itemVat;
  if (settings?.include_vat) return Number(settings.vat);
  return 0;
};

const applyVat = (price, vatRate) => round2(price * (1 + vatRate / 100));

export const getMenuPriceInVAT = (menu, allElements, settings) => {
  const menuTotal = (menu.price || 0) * (menu.quantity || 1);
  const menuRate = getVATRate(menu.vat, settings);
  const childElements = allElements.filter(el => el.parent_dms_nr === menu.dms_nr);
  if (!childElements.some(el => el.price && el.price > 0)) return applyVat(menuTotal, menuRate);

  const childElementsSum = childElements.reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);
  if (round2(childElementsSum) !== round2(menuTotal)) return applyVat(menuTotal, menuRate);

  const groups = new Map();

  childElements.forEach(el => {
    const rate = getVATRate(el.vat, settings);
    const total = (el.price || 0) * (el.quantity || 1);
    groups.set(rate, (groups.get(rate) || 0) + total);
  });

  let result = 0;

  groups.forEach((amount, rate) => {
    result += applyVat(amount, rate);
  });

  return round2(result);
};

export const getInterventionPriceWithVAT = (intervention, settings) => {
  const interventionRate = getVATRate(intervention.vat, settings);
  const interventionElements = intervention.elements || [];
  if (!interventionElements.length || !interventionElements.some(el => el.price && el.price > 0)) return applyVat(intervention.price, interventionRate);

  const topLevelElements = interventionElements.filter(el => !el.parent_dms_nr);
  const topLevelElementsSum = topLevelElements
    .filter(el => el.element_type !== INTERVENTION_ELEMENT_TYPE.MENU)
    .reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  const menuSum = topLevelElements.filter(el => el.element_type === INTERVENTION_ELEMENT_TYPE.MENU).reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);
  if (round2(topLevelElementsSum + menuSum) !== round2(intervention.price)) return applyVat(intervention.price, interventionRate);

  const nonMenuGroups = new Map();

  const result =
    topLevelElements.reduce((sum, el) => {
      if (el.element_type === INTERVENTION_ELEMENT_TYPE.MENU) return sum + getMenuPriceInVAT(el, interventionElements, settings);
      const rate = getVATRate(el.vat, settings);
      const total = (el.price || 0) * (el.quantity || 1);
      nonMenuGroups.set(rate, (nonMenuGroups.get(rate) || 0) + total);
      return sum;
    }, 0) + Array.from(nonMenuGroups).reduce((sum, [rate, amount]) => sum + applyVat(amount, rate), 0);

  return round2(result);
};

const getMenuVATCalculation = (menu, allElements, settings) => {
  const menuTotal = (menu.price || 0) * (menu.quantity || 1);
  const menuRate = getVATRate(menu.vat, settings);
  const childElements = allElements.filter(el => el.parent_dms_nr === menu.dms_nr);
  if (!childElements.some(el => el.price && el.price > 0)) return [{ rate: menuRate, amount: menuTotal }];

  const childElementsSum = childElements.reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);
  if (round2(childElementsSum) !== round2(menuTotal)) return [{ rate: menuRate, amount: menuTotal }];

  const groups = new Map();

  childElements.forEach(el => {
    const rate = getVATRate(el.vat, settings);
    const total = (el.price || 0) * (el.quantity || 1);
    groups.set(rate, (groups.get(rate) || 0) + total);
  });

  return Array.from(groups, ([rate, amount]) => ({ rate, amount }));
};

const getInterventionVATCalculation = (intervention, settings) => {
  const interventionRate = getVATRate(intervention.vat, settings);
  const interventionElements = intervention.elements || [];
  if (!interventionElements.length || !interventionElements.some(el => el.price && el.price > 0)) return [{ rate: interventionRate, amount: intervention.price }];

  const topLevelElements = interventionElements.filter(el => !el.parent_dms_nr);
  const topLevelElementsSum = topLevelElements
    .filter(el => el.element_type !== INTERVENTION_ELEMENT_TYPE.MENU)
    .reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  const menuElementsSum = topLevelElements
    .filter(el => el.element_type === INTERVENTION_ELEMENT_TYPE.MENU)
    .reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  if (round2(topLevelElementsSum + menuElementsSum) !== round2(intervention.price)) return [{ rate: interventionRate, amount: intervention.price }];

  const groups = new Map();

  topLevelElements.forEach(el => {
    if (el.element_type === INTERVENTION_ELEMENT_TYPE.MENU)
      getMenuVATCalculation(el, interventionElements, settings).forEach(g => {
        groups.set(g.rate, (groups.get(g.rate) || 0) + g.amount);
      });
    else {
      const rate = getVATRate(el.vat, settings);
      const total = (el.price || 0) * (el.quantity || 1);
      groups.set(rate, (groups.get(rate) || 0) + total);
    }
  });

  return Array.from(groups, ([rate, amount]) => ({ rate, amount }));
};

export const getInterventionTotals = (interventions, settings) => {
  let totalAmount = 0;
  let approvedTotal = 0;
  const groupsMap = new Map();

  interventions.forEach(intervention => {
    if (settings?.include_vat) {
      const priceInVAT = getInterventionPriceWithVAT(intervention, settings);
      totalAmount += priceInVAT;
      if (intervention.customer_ok) approvedTotal += priceInVAT;

      getInterventionVATCalculation(intervention, settings).forEach(g => {
        groupsMap.set(g.rate, (groupsMap.get(g.rate) || 0) + g.amount);
      });
    } else {
      totalAmount += intervention.price;
      if (intervention.customer_ok) approvedTotal += intervention.price;
    }
  });

  const vatGroups = Array.from(groupsMap, ([rate, amount]) => ({
    rate,
    amount: round2((amount * rate) / 100),
    total: round2(amount * (1 + rate / 100)),
  }));

  return {
    vatGroups,
    totalAmount: round2(totalAmount),
    approvedTotal: round2(approvedTotal),
  };
};

export const calculateVatGroups = (vatGroups, additionalPrices, settings) => {
  if (additionalPrices <= 0 || !settings?.include_vat) return vatGroups;

  const locationRate = Number(settings.vat);
  if (!locationRate) return vatGroups;

  const additionalVat = round2((additionalPrices * locationRate) / 100);
  const existing = vatGroups.find(g => g.rate === locationRate);
  if (existing)
    return vatGroups.map(g =>
      g.rate === locationRate ? { ...g, amount: round2(g.amount + additionalVat), total: round2(g.total + additionalPrices + additionalVat) } : g
    );

  return [...vatGroups, { rate: locationRate, amount: additionalVat, total: round2(additionalPrices + additionalVat) }];
};

export const getPhoneNumber = communication => {
  let phoneNr = "";

  if (communication.customer) {
    if (communication.customer.tel_mobile_private) phoneNr = communication.customer.tel_mobile_private;

    if (communication.customer.tel_private_nr) phoneNr = communication.customer.tel_private_nr;

    if (communication.customer.tel_mobile_business) phoneNr = communication.customer.tel_mobile_business;

    if (communication.customer.tel_business_nr) phoneNr = communication.customer.tel_business_nr;
  }

  return phoneNr;
};

export const getEmail = communication => {
  const event = communication.events && communication.events.sort((a, b) => (a.created_on < b.created_on ? 1 : -1)).find(e => e.receivers?.length);
  if (event) {
    let receiver = event.receivers.find(r => r.channel === 1);
    if (receiver) return receiver.destination;
  }

  if (communication.customer) {
    if (communication.customer.email_private) return communication.customer.email_private;

    if (communication.customer.email_business) return communication.customer.email_business;
  }

  return "";
};

export function normalizeCommunicationData(communication, i18n) {
  if (communication.settings) i18n.changeLanguage(communication.settings.language_code || "en-GB");

  if (communication && communication.appointment) {
    document.title = communication.appointment.car_make + " " + communication.appointment.car_model + " " + communication.appointment.reg_number;

    if (!Array.isArray(communication.agreements)) communication.agreements = [];

    if (!Array.isArray(communication.check_in_results)) communication.check_in_results = [];
    if (!Array.isArray(communication.results)) communication.results = [];

    let withTyres = communication.results.filter(r => r.tyre_position > 0);
    if (withTyres.length > 0) {
      let latestCheck = withTyres[0].check_id;
      if (withTyres.some(t => t.check_id !== latestCheck)) {
        withTyres.forEach(t => {
          if (t.check_id > latestCheck) latestCheck = t.check_id;
        });

        communication.results = communication.results.filter(r => r.tyre_position === 0 || r.check_id === latestCheck);
      }
    }

    communication.results.forEach(r => {
      if (!r.answer_item) r.answer_item = {};
      if (r.customer_approved === undefined) r.customer_approved = false;
      if (!r.tyre_replacements) r.tyre_replacements = [];
      if (!r.tyre) r.tyre = {};
    });

    communication.results = communication.results.filter(
      item => item.question_status > -1 || item.tyre_position > 0 || ["signature", "employee_signature", "customer_signature"].includes(item.answer_item.template)
    );

    communication.results.sort((a, b) => {
      if (a.checklist_type === 4 && b.checklist_type !== 4) return -1;
      else if (a.checklist_type !== 4 && b.checklist_type === 4) return 1;

      if (a.checklist_order < b.checklist_order) return -1;
      else if (a.checklist_order > b.checklist_order) return 1;

      if (a.check_id < b.check_id) return -1;
      else if (a.check_id > b.check_id) return 1;

      if (a.group_order < b.group_order) return -1;
      else if (a.group_order > b.group_order) return 1;

      if (a.group_name < b.group_name) return -1;
      else if (a.group_name > b.group_name) return 1;

      if (a.question_order < b.question_order) return -1;
      else if (a.question_order > b.question_order) return 1;

      if (a.question_result_id < b.question_result_id) return -1;
      else if (a.question_result_id > b.question_result_id) return 1;

      if (a.tyre_position < b.tyre_position) return -1;
      else if (a.tyre_position > b.tyre_position) return 1;

      return a.title < b.title ? -1 : 1;
    });

    if (!Array.isArray(communication.appointment.interventions)) communication.appointment.interventions = [];
    if (!Array.isArray(communication.appointment.notes)) communication.appointment.notes = [];

    const sortInterventionsByDmsNrOrId = (i1, i2) => {
      if (i1.dms_nr && i2.dms_nr) {
        if (i1.dms_nr !== i2.dms_nr) return i1.dms_nr.localeCompare(i2.dms_nr, undefined, { numeric: true, sensitivity: "base" });
      } else if (!i1.dms_nr && i2.dms_nr) return 1;
      else if (i1.dms_nr && !i2.dms_nr) return -1;

      return i1.id - i2.id;
    };

    communication.appointment.interventions.sort((i1, i2) => {
      if (i1.question_result_status === i2.question_result_status) return sortInterventionsByDmsNrOrId(i1, i1);

      if (isNaN(i1.question_result_status)) return -1;
      if (isNaN(i2.question_result_status)) return 1;

      if (i1.question_result_status === QUESTION_RESULT_STATUS.NOT_WORKED_ON) return 1;
      if (i2.question_result_status === QUESTION_RESULT_STATUS.NOT_WORKED_ON) return -1;

      return i2.question_result_status - i1.question_result_status;
    });

    communication.appointment.interventions.forEach((i, j) => {
      i.intervention_index = j;

      if (!i.elements) i.elements = [];
      else {
        i.elements.sort((a, b) => {
          if (!a.dms_nr && !b.dms_nr) return a.id - b.id;

          if (a.parent_dms_nr === b.parent_dms_nr) return a.dms_nr.localeCompare(b.dms_nr, undefined, { numeric: true, sensitivity: "base" });

          if ((a.parent_dms_nr === "") !== (b.parent_dms_nr === "")) {
            if (a.parent_dms_nr === b.dms_nr) return 1;
            else if (b.parent_dms_nr === a.dms_nr) return -1;
            return (a.parent_dms_nr || a.dms_nr).localeCompare(b.parent_dmsn_nr || b.dms_nr, undefined, { numeric: true, sensitivity: "base" });
          }

          return a.parent_dms_nr.localeCompare(b.parent_dmsn_nr, undefined, { numeric: true, sensitivity: "base" });
        });
      }
    });

    if (!Array.isArray(communication.check_in_remarks)) communication.check_in_remarks = [];
    if (!Array.isArray(communication.diagnose_overview_remarks)) communication.diagnose_overview_remarks = [];
  } else return false;

  return true;
}

export function getItemName(t, r) {
  if (!r.tyre_position || TYRE_POSITION.IS_TRUCK_TYRE(r.tyre_position)) return r.title;

  return getTyreItemName(t, r);
}

export function renderCarRegistration(country, reg_nr) {
  const countryCode = ["BE", "FR", "LU"].includes(country) ? country[0] : country;

  return (
    <div className={`header-info-reg-num-box ${country || ""}`}>
      <div className="header-info-reg-country">
        <div className="header-info-reg-country-text">{countryCode || ""}</div>
      </div>

      <div className="header-info-reg-num">{reg_nr.split("-").join(" - ")}</div>
    </div>
  );
}

export const getYYYMMDD = () => {
  let now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  let d = now.getDate();
  let mm = m < 10 ? "0" + m : m;
  let dd = d < 10 ? "0" + d : d;
  return "" + y + mm + dd;
};

export const getImageAndVideoFileTypes = files => {
  let images = [],
    videos = [];

  if (files?.length) {
    // The regEx fallback workaround needs to be removed once the file.type field issue is resolved
    images = files.filter(file => (file.type ? file.type.startsWith("image/") : /\.(gif|jpg|jpeg|png)/i.test(file.url)));
    videos = files.filter(file => (file.type ? file.type.startsWith("video/") : /\.(avi|mov|wmv|amv|mpeg|mkv|mp4)/i.test(file.url)));
  }

  return {
    images,
    videos,
  };
};

export const getServicePricePlaceholder = (item, settings, t) => {
  if (item.pin_type_id === PIN_TYPE.WARRANTY && (settings.hide_warranty_intervention_price || settings.cannot_decline_warranty_types))
    return t("cc_warranty").message || "Warranty";
  if (item.pin_type_id === PIN_TYPE.RECALL && settings.hide_recall_intervention_price) return t("cc_recall").message || "Recall";
};
